import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCategories, useTrending } from '../../hooks';
import { formatDate } from '../../utils/helpers';
import { getSettings } from '../../utils/api';
import './Footer.css';

const DEFAULT_FOOTER = {
  siteName: 'RIN-NodeJS',
  tagline: 'Incisive journalism. Authoritative analysis.\nDelivered daily since 2025.',
  socialLinks: [
    { id: 's1', label: 'Twitter',  icon: 'X',  href: '#' },
    { id: 's2', label: 'LinkedIn', icon: 'in', href: '#' },
    { id: 's3', label: 'RSS',      icon: '⊛',  href: '#' },
  ],
  telegramTitle: 'Join Our Community',
  telegramDesc:  'Get the latest news and updates instantly. Join our Telegram channel.',
  telegramLink:  'https://t.me/+MRvlsjhc0YRmOTk1',
  copyright:     '© {year} RIN-NodeJS. All rights reserved.',
  bottomLinks: [
    { id: 'b1', label: 'Accessibility', href: '#' },
    { id: 'b2', label: 'Site Map',      href: '#' },
    { id: 'b3', label: 'Archive',       href: '#' },
  ],
};

const Footer = () => {
  const { data: categoriesData } = useCategories();
  const { data: trendingData } = useTrending();
  const [fs, setFs] = useState(DEFAULT_FOOTER);

  useEffect(() => {
    getSettings().then(res => {
      const loaded = res.data?.footerSettings;
      if (loaded) setFs(prev => ({ ...prev, ...loaded }));
    }).catch(() => {});
  }, []);

  const categories = (categoriesData?.data || []).filter(c => !c.parentId);
  const popular = (trendingData?.data || []).slice(0, 5);
  const copyrightText = (fs.copyright || '').replace('{year}', new Date().getFullYear());

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="container footer-top-inner">

          {/* Brand */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">{fs.siteName}</Link>
            <p className="footer-tagline">
              {fs.tagline.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i < fs.tagline.split('\n').length - 1 && <br />}</React.Fragment>
              ))}
            </p>
            <div className="footer-social">
              {(fs.socialLinks || []).filter(s => s.label).map(s => (
                <a key={s.id} href={s.href || '#'} className="social-link" aria-label={s.label}>
                  {s.icon || s.label.slice(0, 2)}
                </a>
              ))}
            </div>
          </div>

          {/* Links grid */}
          <div className="footer-links-grid">

            {/* Col 1 — Categories */}
            <div className="footer-links-col">
              <h4 className="footer-col-title">Categories</h4>
              <ul>
                {categories.map(cat => (
                  <li key={cat.id}>
                    <Link to={`/category/${cat.slug}`} className="footer-link">
                      <span className="footer-cat-dot" style={{ background: cat.color }} />
                      {cat.name}
                    </Link>
                  </li>
                ))}
                {!categories.length && (
                  <li><Link to="/" className="footer-link">Home</Link></li>
                )}
              </ul>
            </div>

            {/* Col 2 — Popular News */}
            <div className="footer-links-col">
              <h4 className="footer-col-title">Popular News</h4>
              <div className="footer-popular-list">
                {popular.map(a => (
                  <Link key={a.id} to={`/article/${(a.id||'').slice(0,8)}`} className="footer-popular-item">
                    {a.image && (
                      <div className="footer-popular-thumb">
                        <img src={a.image} alt={a.title} loading="lazy" />
                      </div>
                    )}
                    <div className="footer-popular-body">
                      <p className="footer-popular-title">{a.title}</p>
                      <span className="footer-popular-date">
                        {formatDate(a.publishedAt || a.createdAt, 'short')}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Telegram */}
          <div className="footer-newsletter">
            <h4 className="footer-col-title">{fs.telegramTitle}</h4>
            <p className="footer-newsletter-desc">{fs.telegramDesc}</p>
            <a href={fs.telegramLink} target="_blank" rel="noreferrer" className="footer-tg-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.48 14.607l-2.95-.924c-.642-.204-.657-.642.136-.953l11.57-4.461c.537-.194 1.006.131.326.979z"/>
              </svg>
              Join Us on Telegram
            </a>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <p className="footer-copyright">{copyrightText}</p>
          <div className="footer-bottom-links">
            {(fs.bottomLinks || []).filter(l => l.label).map((link, i) => (
              <React.Fragment key={link.id}>
                {i > 0 && <span>·</span>}
                <a href={link.href || '#'} className="footer-link-sm">{link.label}</a>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
